import { useEffect, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sidebar } from "./components/Sidebar";
import { Icon } from "./components/Icon";
import { RouteTransition } from "./components/RouteTransition";
import { AppAiFab } from "./components/AppAiFab";
import { DemoEntryToast } from "@/features/demo/DemoEntryToast";
import { InstallPrompt } from "@/features/pwa/InstallPrompt";
import { PlannerProvider } from "@/features/ai-planner/PlannerProvider";
import { ImpersonationBanner } from "@/features/admin/ImpersonationBanner";
import { useShellDrawerStore } from "@/stores/shell-drawer-store";

const ROUTE_LABEL_KEYS: { prefix: string; key: string }[] = [
  { prefix: "/app/dashboard", key: "nav.dashboard" },
  { prefix: "/app/guests", key: "nav.guests" },
  { prefix: "/app/seating", key: "nav.seating" },
  { prefix: "/app/tasks", key: "nav.tasks" },
  { prefix: "/app/budget", key: "nav.budget" },
  { prefix: "/app/accommodation", key: "nav.accommodation" },
  { prefix: "/app/day-plan", key: "nav.dayPlan" },
  { prefix: "/app/papeteria", key: "nav.papeteria" },
  { prefix: "/app/website", key: "nav.website" },
  { prefix: "/app/ai", key: "nav.ai" },
  { prefix: "/app/settings", key: "nav.settings" },
];

function useRouteLabel(): string | null {
  const { pathname } = useLocation();
  const match = ROUTE_LABEL_KEYS.find((r) => pathname.startsWith(r.prefix));
  return match?.key ?? null;
}

export function AppShell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const plannerFullWidth = pathname.endsWith("/seating") || pathname.startsWith("/app/ai");
  const drawerOpen = useShellDrawerStore((s) => s.open);
  const closeDrawer = useShellDrawerStore((s) => s.closeDrawer);
  const toggleDrawer = useShellDrawerStore((s) => s.toggleDrawer);
  const routeLabelKey = useRouteLabel();

  // Close drawer on route change
  useEffect(() => {
    closeDrawer();
  }, [pathname, closeDrawer]);

  // Reflect open state on <body> so the CSS in tokens.css can target it.
  useEffect(() => {
    if (drawerOpen) {
      document.body.setAttribute("data-drawer-open", "true");
      document.body.style.overflow = "hidden";
    } else {
      document.body.removeAttribute("data-drawer-open");
      document.body.style.overflow = "";
    }
    return () => {
      document.body.removeAttribute("data-drawer-open");
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  return (
    <PlannerProvider>
      <div className="app">
        <a href="#app-main" className="skip-link">
          {t("shell.skipToMain")}
        </a>
        <DemoEntryToast />
        {drawerOpen && <div className="drawer-backdrop" onClick={closeDrawer} aria-hidden />}
        <Sidebar onNavigate={closeDrawer} />
        <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <ImpersonationBanner />
          {!plannerFullWidth && (
            <header className="mobile-topbar">
              <div className="mobile-topbar-left">
                <button
                  type="button"
                  className="btn-icon"
                  aria-label={drawerOpen ? t("shell.closeMenu") : t("shell.openMenu")}
                  onClick={toggleDrawer}
                >
                  <Icon name={drawerOpen ? "x" : "menu"} size={16} />
                </button>
                {routeLabelKey && <span className="mobile-topbar-route">{t(routeLabelKey)}</span>}
              </div>
              <NavLink to="/app/dashboard" className="mobile-topbar-brand">
                amazing<em>.</em>wedding
              </NavLink>
            </header>
          )}
          {!plannerFullWidth && <InstallPrompt />}
          <main id="app-main" className={plannerFullWidth ? "main main--planner" : "main"}>
            <RouteTransition variant={plannerFullWidth ? "planner" : "default"}>
              {children}
            </RouteTransition>
          </main>
        </div>
        {!plannerFullWidth && <AppAiFab />}
      </div>
    </PlannerProvider>
  );
}
